import { Box, Icon, Notification, smBumps, Typo, xxlBumps } from '@wrisk/ui-components'
import React, { FunctionComponent } from 'react'

import {
  getCurrentPolicyVersion,
  isRenewalPolicy,
  PolicyAdjustmentResponse,
} from '../../../../../domain'
import {
  TKey,
  useWriskTranslation,
} from '../../../../../infrastructure/internationalisation'
import { ManagementConfig, ProductKind } from '../../../../../state/configuration'
import {
  AdjustedPremiumSection,
  ProrataPremiumSection,
} from '../../../../organisms/policy'
import { PromotionRemovedBanner } from '../../../../organisms/promotion'
import { FullPage } from '../../../../templates'
import { useProduct } from '../../../productContext'
import { usePolicy } from '../../PolicyProvider'
import { AdjustmentActionBar } from './AdjustmentActionBar'
import { AdjustmentConfigChanges } from './AdjustmentConfigChanges'
import { AdjustmentResponseChanges } from './AdjustmentResponseChanges'

export interface AllowedPolicyAdjustmentPageProps {
  adjustment: PolicyAdjustmentResponse
  config?: ManagementConfig
  onRepeat: () => void
}

const tKey = TKey('pages.allowed-policy-adjustment')

export const AllowedPolicyAdjustmentPage: FunctionComponent<
  AllowedPolicyAdjustmentPageProps
> = ({ adjustment, config, onRepeat }) => {
  const { t } = useWriskTranslation()
  const { product } = useProduct()
  const { policy } = usePolicy()

  const updatedPolicyVersion = getCurrentPolicyVersion(adjustment.updatedPolicyVersions)

  return (
    <FullPage
      pageId='allowed-adjustment-page'
      header={t(tKey('header'))}
      subheader={t(tKey('subheader'))}
    >
      <Box width={1} display='flex' flexDirection='column' rowGap={xxlBumps}>
        {config ? (
          <AdjustmentConfigChanges config={config} result={adjustment} />
        ) : (
          <AdjustmentResponseChanges result={adjustment} />
        )}

        <PromotionRemovedBanner updatedPolicyVersion={updatedPolicyVersion} />

        <AdjustedPremiumSection
          updatedPolicyVersions={adjustment.updatedPolicyVersions}
        />
        <ProrataPremiumSection adjustment={adjustment} />

        {product.productKind === ProductKind.DRIVEAWAY && (
          <Notification type='standalone'>
            <Typo>{t(tKey(`driveaway-only`))}</Typo>
          </Notification>
        )}

        {isRenewalPolicy(policy) && (
          <Notification type='standalone' variant='warning' id='renewal-notification'>
            <Icon icon='info' mr={smBumps} /> {t(tKey(`renewal-window`))}
          </Notification>
        )}

        <AdjustmentActionBar adjustment={adjustment} onRepeat={onRepeat} />
      </Box>
    </FullPage>
  )
}
